import React from 'react';
import { ArrowRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { getScenarioLabel, getActivityLabel } from '@/utils/labels';

/**
 * Piezas del seguimiento de un alumno (retest y pruebas funcionales). Las usan
 * la ficha del alumno y la pantalla de cada entrevista, para que las dos
 * muestren lo mismo de la misma manera.
 */

/**
 * Severidad de cada escenario, de menos a más cauteloso. Es el mismo orden que
 * usa el motor en su escala de color (verde → amarillo claro → amarillo →
 * naranja → rojo). Sirve para saber si entre dos entrevistas el escenario se
 * volvió más permisivo o más prudente.
 */
const SEVERIDAD = {
  PUEDE_INICIAR: 0,
  APTO_PARA_EJERCICIO_MODERADO: 1,
  APTO_PARA_PROGRESAR_CON_CONTROL: 2,
  APTO_REQUERIDO: 3,
  DERIVACION_INMEDIATA: 4,
  // Alias de registros anteriores.
  APTO_RECOMENDABLE: 1,
  APTO_PARA_PROGRESAR: 2,
};

/** Nivel de actividad, de menos a más activo. */
const NIVEL_ACTIVIDAD = {
  sedentario: 0,
  sedentary: 0,
  irregular: 1,
  insuficientemente_activa: 1,
  insuficientemente_activo: 1,
  activo_regular: 2,
  activa_regular: 2,
};

// Variables que se comparan entre entrevistas. Se muestra el cambio en crudo,
// sin dictaminar si mejoró o empeoró: los umbrales de relevancia clínica los
// define el profesional, no el sistema.
const COMPARABLES = [
  { key: 'weight', label: 'Peso', unidad: 'kg', decimales: 1 },
  { key: 'imc', label: 'IMC', unidad: '', decimales: 1 },
  { key: 'blood_pressure_systolic', label: 'Presión sistólica', unidad: 'mmHg', decimales: 0 },
  { key: 'blood_pressure_diastolic', label: 'Presión diastólica', unidad: 'mmHg', decimales: 0 },
  { key: 'resting_heart_rate', label: 'FC de reposo', unidad: 'lpm', decimales: 0 },
];

export function formatFecha(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export function num(v) {
  const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : v;
  return Number.isFinite(n) ? n : null;
}

/**
 * Cambio entre dos mediciones. Con color, pero el mismo para subir que para
 * bajar: el signo y la flecha dicen la dirección, y pintar de verde una bajada
 * de peso induciría a leer como mejora lo que puede ser ruido de medición.
 */
function ChipCambio({ delta, texto }) {
  if (delta === null || delta === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  const Icono = delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const tono = delta === 0
    ? 'bg-slate-50 text-slate-500 border-slate-200'
    : 'bg-primary/10 text-primary border-primary/25';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm font-semibold tabular-nums whitespace-nowrap ${tono}`}>
      <Icono className="h-3.5 w-3.5" />
      {texto}
    </span>
  );
}

/** Tabla de comparación: una caja con encabezado de color y filas alternadas. */
function TablaComparativa({ columnas, filas, testid }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-soft" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-primary/[0.07] border-b border-primary/15 text-left">
            {columnas.map((c, i) => (
              <th
                key={i}
                className={`py-2.5 px-4 text-xs font-semibold uppercase tracking-wide ${
                  i === 2 ? 'text-primary' : 'text-slate-600'
                }`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filas.map((f, i) => (
            <tr key={f.clave} className={i % 2 ? 'bg-slate-50/70' : 'bg-white'}>
              <td className="py-2.5 px-4 font-medium text-slate-700">{f.etiqueta}</td>
              <td className="py-2.5 px-4 text-slate-500 tabular-nums">{f.previa}</td>
              <td className="py-2.5 px-4 text-slate-900 font-semibold tabular-nums">{f.actual}</td>
              <td className="py-2.5 px-4"><ChipCambio delta={f.delta} texto={f.cambio} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Qué cambió entre dos entrevistas: escenario, nivel de actividad y variables. */
export function ComparativaEntrevistas({ anterior, actual }) {
  const estadoAct = actual?.exercise_recommendation?.estado;
  const estadoAnt = anterior?.exercise_recommendation?.estado;
  const sevAct = SEVERIDAD[estadoAct];
  const sevAnt = SEVERIDAD[estadoAnt];
  let cambioEscenario = null;
  if (sevAct !== undefined && sevAnt !== undefined) {
    if (sevAct < sevAnt) {
      cambioEscenario = {
        tono: 'bg-emerald-50 border-emerald-200 text-emerald-900',
        titulo: 'Mejoró el escenario',
        detalle: 'El sistema habilita ahora más intensidad que en la entrevista anterior.',
      };
    } else if (sevAct > sevAnt) {
      cambioEscenario = {
        tono: 'bg-amber-50 border-amber-200 text-amber-900',
        titulo: 'El escenario se volvió más prudente',
        detalle: 'Cambió algo en la información declarada respecto de la entrevista anterior.',
      };
    } else {
      cambioEscenario = {
        tono: 'bg-slate-50 border-slate-200 text-slate-800',
        titulo: 'El escenario se mantiene',
        detalle: 'La conducta operativa sugerida no cambió.',
      };
    }
  }

  const actAct = NIVEL_ACTIVIDAD[actual?.activity_level];
  const actAnt = NIVEL_ACTIVIDAD[anterior?.activity_level];
  const subioActividad = actAct !== undefined && actAnt !== undefined && actAct > actAnt;

  return (
    <div className="space-y-4" data-testid="comparativa-entrevistas">
      {/* Escenario: el titular */}
      {cambioEscenario && (
        <div className={`rounded-lg border px-4 py-3 ${cambioEscenario.tono}`}>
          <p className="font-semibold">{cambioEscenario.titulo}</p>
          <p className="text-sm mt-1 flex flex-wrap items-center gap-2">
            <span className="opacity-70">{getScenarioLabel(estadoAnt)}</span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium">{getScenarioLabel(estadoAct)}</span>
          </p>
          <p className="text-sm mt-1.5 opacity-80">{cambioEscenario.detalle}</p>
        </div>
      )}

      {/* Actividad física: el motor la usa como entrada, así que un cambio
          acá es lo que suele mover el escenario */}
      {actAct !== undefined && actAnt !== undefined && (
        <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-3">
          <p className="text-sm text-sky-800/80">Nivel de actividad física</p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-slate-800">
            <span className="opacity-70">{getActivityLabel(anterior.activity_level)}</span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium">{getActivityLabel(actual.activity_level)}</span>
            {subioActividad && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Subió de categoría
              </span>
            )}
          </p>
        </div>
      )}

      {/* Variables medidas */}
      <TablaComparativa
        testid="tabla-variables"
        columnas={['Variable', formatFecha(anterior.created_at), formatFecha(actual.created_at), 'Cambio']}
        filas={COMPARABLES.map((c) => {
          const a = num(anterior[c.key]);
          const u = num(actual[c.key]);
          const delta = a !== null && u !== null ? u - a : null;
          return {
            clave: c.key,
            etiqueta: c.label,
            previa: a !== null ? a.toFixed(c.decimales) : '—',
            actual: u !== null ? u.toFixed(c.decimales) : '—',
            delta,
            cambio: delta === null ? null
              : `${delta > 0 ? '+' : ''}${delta.toFixed(c.decimales)} ${c.unidad}`.trim(),
          };
        })}
      />
      <p className="text-xs text-gray-400">
        Los cambios se muestran tal cual. Definir a partir de qué diferencia una
        variación es clínicamente relevante es criterio profesional, no del sistema.
      </p>
    </div>
  );
}

/** Lo que la persona declaró haber hecho desde la entrevista anterior. */
export function AdherenciaRetest({ adherencia }) {
  if (!adherencia) return null;
  const a = adherencia;
  const intensidad = a.intensidad_percibida
    ? a.intensidad_percibida.charAt(0).toUpperCase() + a.intensidad_percibida.slice(1)
    : null;
  const datos = [
    ['Sesiones por semana', a.sesiones_por_semana],
    ['Minutos por sesión', a.minutos_por_sesion != null ? `${a.minutos_por_sesion} min` : null],
    ['Minutos por semana', a.minutos_semanales != null ? `${a.minutos_semanales} min` : null],
    ['Semanas cumplidas', a.semanas_cumplidas],
    ['Intensidad percibida', intensidad],
  ];
  return (
    <div className="space-y-2" data-testid="adherencia-retest">
      <p className="text-sm font-medium text-slate-600">Adherencia declarada desde la entrevista anterior</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {datos.map(([label, valor]) => (
          <div key={label} className="rounded-lg border border-primary/20 bg-primary/[0.05] px-3 py-2">
            <p className="text-[11px] text-primary/80 font-medium leading-tight">{label}</p>
            <p className="text-base font-bold text-slate-800 mt-0.5 tabular-nums">{valor ?? '—'}</p>
          </div>
        ))}
      </div>
      {a.observaciones && (
        <p className="text-xs text-slate-500">
          <span className="font-medium">Observaciones:</span> {a.observaciones}
        </p>
      )}
    </div>
  );
}

/** "basal → final" de un registro de la caminata, o null si no se cargó. */
function par(sg, a, b) {
  if (sg?.[a] == null && sg?.[b] == null) return null;
  return `${sg?.[a] ?? '—'} → ${sg?.[b] ?? '—'}`;
}

/** Registros del día de la caminata (FC, saturación, Borg), en una línea. */
function signosC6m(sg) {
  const partes = [
    par(sg, 'fc_basal', 'fc_final') && `FC ${par(sg, 'fc_basal', 'fc_final')} lpm`
      + (sg.fc_recuperacion != null ? ` (recuperación ${sg.fc_recuperacion})` : ''),
    par(sg, 'sat_basal', 'sat_final') && `Saturación ${par(sg, 'sat_basal', 'sat_final')} %`,
    par(sg, 'borg_disnea_basal', 'borg_disnea_final') && `Borg disnea ${par(sg, 'borg_disnea_basal', 'borg_disnea_final')}`,
    par(sg, 'borg_fatiga_basal', 'borg_fatiga_final') && `Borg fatiga ${par(sg, 'borg_fatiga_basal', 'borg_fatiga_final')}`,
  ].filter(Boolean);
  return partes.length ? partes.join(' · ') : null;
}

/**
 * Pruebas funcionales: comparativa de cada prueba contra su toma anterior y el
 * último resultado de cada una. Se busca la última toma de cada prueba, no la
 * última sesión: si en la última solo se hizo dinamometría, la caminata se
 * sigue mostrando con su fecha.
 */
export function PruebasFuncionalesDetalle({ pruebas, comparativa }) {
  if (!pruebas?.length) return null;
  const ultimaDe = (clave) => pruebas.find((m) => m.resultados?.[clave]);
  const filas = [];
  const c6mMed = ultimaDe('c6m');
  if (c6mMed) {
    const c6m = c6mMed.resultados.c6m;
    // Se juntan el hallazgo clínico y los avisos del cálculo (distancia
    // implausible, pasillo, detención, desaturación, edad fuera de la muestra).
    const avisosC6m = [
      c6m.bajo_lin === true ? 'Por debajo del límite inferior de normalidad' : null,
      ...(c6m.avisos || []),
    ].filter(Boolean);
    const detalle = [
      c6m.porcentaje_teorico != null
        ? `${c6m.porcentaje_teorico} % del teórico (${c6m.teorico_m} m)`
        : c6m.sin_referencia_motivo,
      signosC6m(c6m.signos),
    ].filter(Boolean).join(' · ');
    filas.push([
      'Caminata de 6 minutos',
      c6m.distancia_m != null ? `${c6m.distancia_m} m` : '—',
      detalle || null,
      avisosC6m.length ? avisosC6m.join(' · ') : null,
      c6mMed.created_at,
    ]);
  }
  const astMed = ultimaDe('astrand');
  if (astMed) {
    const ast = astMed.resultados.astrand;
    filas.push([
      'Escalón de Åstrand-Ryhming',
      ast.vo2max_l != null ? `${ast.vo2max_l} L/min` : '—',
      ast.vo2max_ml_kg_min != null
        ? `${ast.vo2max_ml_kg_min} ml/kg/min · escalón de ${ast.altura_escalon_cm} cm`
        : ast.no_calculable_motivo,
      ast.betabloqueante && ast.vo2max_l != null ? (ast.avisos || [])[0] : null,
      astMed.created_at,
    ]);
  }
  const spMed = ultimaDe('sentarse_pararse');
  if (spMed) {
    const sp = spMed.resultados.sentarse_pararse;
    filas.push([
      'Sentarse-pararse 30 s',
      sp.repeticiones != null ? `${sp.repeticiones} repeticiones` : '—',
      null, null,
      spMed.created_at,
    ]);
  }
  const hgMed = ultimaDe('handgrip');
  if (hgMed) {
    const hg = hgMed.resultados.handgrip;
    filas.push([
      'Dinamometría de mano',
      hg.mejor_kg != null ? `${hg.mejor_kg} kg` : '—',
      hg.corte_ewgsop2_kg != null ? `corte EWGSOP2: ${hg.corte_ewgsop2_kg} kg` : null,
      hg.por_debajo_de_corte === true
        ? 'Por debajo del corte de fuerza baja del criterio EWGSOP2' : null,
      hgMed.created_at,
    ]);
  }

  return (
    <div className="space-y-4" data-testid="pruebas-funcionales-detalle">
      {pruebas.length === 1 && (
        <p className="text-sm text-gray-500">
          Con una sola medición no hay con qué comparar. La comparativa aparece
          a partir de la segunda.
        </p>
      )}

      {/* Cada prueba contra su toma anterior, con las fechas de las dos */}
      {comparativa?.comparaciones?.length > 0 && (
        <div className="space-y-1.5">
          <TablaComparativa
            testid="tabla-pruebas"
            columnas={['Prueba', 'Anterior', 'Actual', 'Cambio']}
            filas={comparativa.comparaciones.map((c) => ({
              clave: c.prueba,
              etiqueta: c.prueba,
              previa: <>{c.previa} <span className="block text-xs font-normal text-slate-400">{formatFecha(c.fecha_previa)}</span></>,
              actual: <>{c.actual} <span className="block text-xs font-normal text-slate-400">{formatFecha(c.fecha_actual)}</span></>,
              delta: c.cambio,
              cambio: `${c.cambio > 0 ? '+' : ''}${c.cambio} ${c.unidad || ''}`.trim(),
            }))}
          />
          {comparativa.comparaciones.filter((c) => c.aviso).map((c) => (
            <p key={c.prueba} className="text-xs text-amber-800">
              {c.prueba}: {c.aviso}
            </p>
          ))}
        </div>
      )}

      {/* Último resultado de cada prueba */}
      <div className="space-y-2.5">
        <p className="text-sm text-gray-500">Último resultado de cada prueba</p>
        {filas.map(([titulo, valor, detalle, aviso, fecha]) => (
          <div
            key={titulo}
            /* La franja de color de la izquierda es el acento visual.
               Cambia a ámbar solo cuando hay un hallazgo real, así el
               resalte significa algo en vez de decorar. */
            className={`relative overflow-hidden rounded-xl border pl-5 pr-4 py-3.5 shadow-soft transition ${
              aviso
                ? 'border-amber-200 bg-gradient-to-r from-amber-50/80 to-white'
                : 'border-slate-200 bg-gradient-to-r from-slate-50/70 to-white hover:border-slate-300'
            }`}
          >
            <span
              className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                aviso ? 'bg-amber-400' : 'bg-primary/70'
              }`}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-slate-700">
                {titulo}
                <span className="ml-2 text-xs font-normal text-slate-400">{formatFecha(fecha)}</span>
              </span>
              <span
                className={`font-mono text-2xl font-bold tabular-nums ${
                  aviso ? 'text-amber-800' : 'text-slate-800'
                }`}
              >
                {valor}
              </span>
            </div>
            {detalle && <p className="text-xs text-slate-500 mt-1">{detalle}</p>}
            {aviso && (
              <p className="text-xs font-medium text-amber-900 mt-2 bg-amber-100/70 border border-amber-200 rounded-lg px-2.5 py-1.5">
                {aviso}
              </p>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400">
        Los cambios se muestran tal cual. Definir a partir de qué diferencia una
        variación es clínicamente relevante es criterio profesional.
      </p>
    </div>
  );
}
